// src/shared/hooks/usePublicQueue.js
import { useEffect, useMemo, useState } from "react";
import { collection, query, where, orderBy, onSnapshot, doc } from "firebase/firestore";
import { db } from "../../firebase/firebase";

const DBG = (t,p)=>{try{window.__LINE_DBG_PUSH?.({type:t,...p});}catch{}};

export default function usePublicQueue({ userId = null, userIdField = "lineId" } = {}) {
  // ---- runtime（受付ON/OFF・待ち人数・時計） ----
  const [runtimeReady, setRuntimeReady] = useState(false);
  const [inhouseOn, setInhouseOn]     = useState(null);   // true/false/null
  const [onlineOn, setOnlineOn] = useState(null);   // null=未取得, true/false

  const [waitCount, setWaitCount]     = useState(null);
  const [nowStr, setNowStr]           = useState("");

  useEffect(() => {
  let settleTimer = null;

  const ref = doc(db, "admin", "runtime");
  const unsub = onSnapshot(
    ref,
    { includeMetadataChanges: true },
    (snap) => {
      const d = snap.data() || {};

      // 数値/文字どちらでも安全に数値化
      const wc = Number(d.currentWaitCount);
      setWaitCount(Number.isFinite(wc) ? wc : null);

      setInhouseOn(d.inhouseOn === true);
      setOnlineOn?.(d.onlineOn === true);           // ← ある場合（isOnlineOpen用）

      // サーバ応答が来たら ready に（キャッシュのみの瞬間は待つ）
      if (!snap.metadata.fromCache) {
        clearTimeout(settleTimer);
        setRuntimeReady(true);
      }
    },
    () => setRuntimeReady(true) // エラーでもブロックしない
  );

  // キャッシュしか無いときの保険（1.2sでreadyに）
  settleTimer = setTimeout(() => setRuntimeReady(true), 1200);

  // 時計は 1 秒ごと
  const t = setInterval(() => {
    try {
      const now = new Date();
      const fmt = new Intl.DateTimeFormat("ja-JP", {
        timeZone: "Asia/Tokyo", hour12: false,
        weekday: "short", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
      const parts = Object.fromEntries(fmt.formatToParts(now).map(p => [p.type, p.value]));
      const w = parts.weekday || "日月火水木金土"[now.getDay()];
      setNowStr(`（${parts.month}/${parts.day}（${w}）  ${parts.hour}時${parts.minute}分${parts.second}秒 現在）`);
    } catch {}
  }, 1000);

  return () => { unsub(); clearTimeout(settleTimer); clearInterval(t); };
}, []);

  const isOnlineOpen = (inhouseOn === true) && (onlineOn === true);
  // ---- 受付ON時だけ行を購読 ----
  const [rowsReady, setRowsReady] = useState(false);
  const [rows, setRows] = useState([]); // {id,no,hold,userIdField値}

  useEffect(() => {
    setRows([]); setRowsReady(false);
    if (inhouseOn !== true) return;             // OFFなら購読しない

    const q = query(
      collection(db, "reservation_slots"),
      where("isReserved", "==", true),
      where("canceled", "==", false),
      where("isReceptionAccepted", "==", false),
      where("canceledLineAt", "==", null),
      orderBy("reservationNumber", "asc")
    );

    const unsub = onSnapshot(q, { includeMetadataChanges: true }, (snap) => {
      if (snap.empty && snap.metadata.fromCache) return;  // サーバ応答待ち
      const next = [];
      snap.forEach(s => {
        const d = s.data();
        if (d.canceledPwaAt)    return;//なぜかqueryに入れると正しく受付データが取得できなくなるので暫定対応
        next.push({
          id: s.id,
          no: d.reservationNumber,
          hold: d.hold === true,
          userId: d[userIdField] || null,
        });
      });
      next.sort((a,b) => (a.no??0)-(b.no??0));
      setRows(next);
      setRowsReady(true);
    }, (e)=>{ DBG("error",{where:"usePublicQueue.rows", error:String(e)}); setRows([]); setRowsReady(true); });

    return () => unsub();
  }, [inhouseOn, userIdField]);

  // ---- 派生：一覧（通常→保留） ----
  const list = useMemo(() => {
    if (!rows?.length) return [];
    const normal = rows.filter(r => r.hold !== true);
    const held   = rows.filter(r => r.hold === true);
    let rank = 0;
    const merged = [...normal, ...held].map(r => ({
      ...r,
      isHold: r.hold === true,
      ahead : r.hold === true ? null : (++rank),
      isMine: userId ? r.userId === userId : false,
    }));
    return merged;
  }, [rows, userId]);

  // ---- 派生：自分の前にいる人数（0ベース） ----
  const myAheadCount = useMemo(() => {
    const mine = list.find(r => r.isMine && !r.isHold);
    if (!mine || mine.ahead == null) return null;
    return Math.max(0, mine.ahead - 1);
  }, [list]);

  // ---- 準備完了フラグ ----
  const readyForList =
    runtimeReady &&
    inhouseOn !== null &&
    (inhouseOn === false || rowsReady);

  // 自分の予約確認が完了したか（受付ON時のみ意味がある）
  const mineResolved =
    inhouseOn === false ? true : (rowsReady && (!!userId ? true : true));

  return {
    // 状態
    runtimeReady, inhouseOn,  onlineOn, isOnlineOpen,waitCount, nowStr,
    rowsReady, list, myAheadCount,
    // 画面用フラグ
    mineResolved, readyForList,
  };
}
